import type { HgSupportedLanguage } from '@smg-real-estate/domus';

import { useSrpJsonLdHead } from '@/composables/useSrpJsonLdHead';
import {
  buildAgencySrpJsonLdGraph,
  buildAgentSrpJsonLdGraph,
  type SrpJsonLdGraph,
  type SrpJsonLdPageMeta,
} from '@/helpers/SEO/buildSrpJsonLdGraph';
import type { DirectoryBreadcrumbLink } from '@/helpers/SEO/directoryBreadcrumb';
import type { Agency } from '@/types';
import type { AgentProfile } from '@/types/agentProfile';

interface DirectorySrpJsonLdSource {
  language: HgSupportedLanguage;
  canonicalUrl: string;
  pageTitle: string;
  pageDescription: string;
  breadcrumbs: DirectoryBreadcrumbLink[];
  isIndexable: boolean;
  locationCount: number;
}

interface DirectorySrpJsonLdSelectors<TSource, TItem> {
  getItems: (source: TSource) => TItem[];
  getCurrentPage: (source: TSource) => number;
  getTotalResults: (source: TSource) => number;
  isLoading: (source: TSource) => boolean;
  blockEmission?: (source: TSource) => boolean;
  buildGraph: (items: TItem[], meta: SrpJsonLdPageMeta) => SrpJsonLdGraph;
}

export interface AgencySrpJsonLdHeadSource extends DirectorySrpJsonLdSource {
  agencies: Agency[];
  agencyListCurrentPage: number;
  totalAgenciesCount: number;
  loadingAgencies: boolean;
}

export interface AgentSrpJsonLdHeadSource extends DirectorySrpJsonLdSource {
  agents: AgentProfile[];
  agentListCurrentPage: number;
  totalAgentsCount: number;
  loadingAgents: boolean;
  agentsLoadFailed: boolean;
}

function useDirectorySrpJsonLdHead<TSource extends DirectorySrpJsonLdSource, TItem>(
  getSource: () => TSource,
  selectors: DirectorySrpJsonLdSelectors<TSource, TItem>,
): void {
  useSrpJsonLdHead({
    isIndexable: () => getSource().isIndexable,
    locationCount: () => getSource().locationCount,
    isLoading: () => selectors.isLoading(getSource()),
    hasResults: () => selectors.getItems(getSource()).length > 0,
    canonicalUrl: () => getSource().canonicalUrl,
    pageTitle: () => getSource().pageTitle,
    pageDescription: () => getSource().pageDescription,
    blockEmission: () => selectors.blockEmission?.(getSource()) ?? false,
    buildGraph: () => {
      const source = getSource();
      const meta: SrpJsonLdPageMeta = {
        canonicalUrl: source.canonicalUrl,
        pageTitle: source.pageTitle,
        pageDescription: source.pageDescription,
        breadcrumbs: source.breadcrumbs,
        currentPage: selectors.getCurrentPage(source),
        totalResultsCount: selectors.getTotalResults(source),
        language: source.language,
      };

      return selectors.buildGraph(selectors.getItems(source), meta);
    },
  });
}

export function useAgencySrpJsonLdHead(getSource: () => AgencySrpJsonLdHeadSource): void {
  useDirectorySrpJsonLdHead(getSource, {
    getItems: (source) => source.agencies,
    getCurrentPage: (source) => source.agencyListCurrentPage,
    getTotalResults: (source) => source.totalAgenciesCount,
    isLoading: (source) => source.loadingAgencies,
    buildGraph: buildAgencySrpJsonLdGraph,
  });
}

export function useAgentSrpJsonLdHead(getSource: () => AgentSrpJsonLdHeadSource): void {
  useDirectorySrpJsonLdHead(getSource, {
    getItems: (source) => source.agents,
    getCurrentPage: (source) => source.agentListCurrentPage,
    getTotalResults: (source) => source.totalAgentsCount,
    isLoading: (source) => source.loadingAgents,
    blockEmission: (source) => source.agentsLoadFailed,
    buildGraph: buildAgentSrpJsonLdGraph,
  });
}
